import React, { useState, useEffect } from 'react';
import { accountingAPI } from '../lib/api';
import { toast } from 'sonner';
import { Loader2, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from './ui/dialog';

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-LK', { style: 'currency', currency: 'LKR' }).format(amount || 0);

export const ClearCancelledReceivablesDialog = ({ open, onOpenChange, onDone }) => {
  const [loading, setLoading] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState({});

  const load = async () => {
    setLoading(true);
    setSelected({});
    try {
      const res = await accountingAPI.getCancelledReceivableCandidates();
      setCandidates(res.data.candidates || []);
    } catch (e) {
      toast.error('Failed to load cancelled receivables');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const selectedIds = Object.keys(selected).filter((id) => selected[id]);
  const selectedTotal = candidates
    .filter((c) => selected[c.order_id])
    .reduce((sum, c) => sum + (c.balance || 0), 0);

  const toggleAll = (checked) => {
    const next = {};
    if (checked) candidates.forEach((c) => (next[c.order_id] = true));
    setSelected(next);
  };

  const clear = async () => {
    if (selectedIds.length === 0) {
      toast.error('Select at least one order');
      return;
    }
    setClearing(true);
    try {
      const res = await accountingAPI.clearCancelledReceivables({ order_ids: selectedIds });
      toast.success(res.data.message || 'Cleared');
      onOpenChange(false);
      if (onDone) onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to clear receivables');
    } finally {
      setClearing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="clear-cancelled-receivables-dialog">
        <DialogHeader>
          <DialogTitle>Clear Cancelled Receivables</DialogTitle>
          <DialogDescription>
            These sales orders are cancelled/returned but still show as unpaid and as a receivable. Select the ones
            to write off — the outstanding receivable ledger entry is reversed and they stop appearing as receivable.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
          </div>
        ) : candidates.length === 0 ? (
          <div className="text-center py-10 text-slate-400" data-testid="no-cancelled-candidates">
            No cancelled orders are currently showing as receivable.
          </div>
        ) : (
          <>
            <div className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-700">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              This reverses the open receivable (Debit Sales Returns / Credit Trade Receivables) and marks the order
              as written off. This action affects your ledger — use it only for genuinely cancelled orders.
            </div>
            <div className="max-h-80 overflow-y-auto mt-3 border rounded-md">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-50">
                  <tr className="text-left text-slate-500 border-b">
                    <th className="py-2 px-2">
                      <Checkbox
                        checked={selectedIds.length === candidates.length && candidates.length > 0}
                        onCheckedChange={toggleAll}
                        data-testid="clear-select-all"
                      />
                    </th>
                    <th className="py-2 pr-4">Date</th>
                    <th className="py-2 pr-4">Order</th>
                    <th className="py-2 pr-4">Customer</th>
                    <th className="py-2 pr-4">Status</th>
                    <th className="py-2 pr-4">Ledger</th>
                    <th className="py-2 pr-4 text-right">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {candidates.map((c) => (
                    <tr key={c.order_id} className="border-b hover:bg-slate-50" data-testid={`cancelled-row-${c.order_id}`}>
                      <td className="py-2 px-2">
                        <Checkbox
                          checked={!!selected[c.order_id]}
                          onCheckedChange={(v) => setSelected((prev) => ({ ...prev, [c.order_id]: v }))}
                          data-testid={`clear-select-${c.order_id}`}
                        />
                      </td>
                      <td className="py-2 pr-4">{c.date}</td>
                      <td className="py-2 pr-4 font-medium text-slate-800">{c.order_number}</td>
                      <td className="py-2 pr-4">{c.customer_name || '-'}</td>
                      <td className="py-2 pr-4">
                        <Badge variant="outline" className="capitalize">{c.status}</Badge>
                      </td>
                      <td className="py-2 pr-4 text-xs text-slate-500">
                        {c.has_ledger_receivable ? 'Will reverse' : 'No entry'}
                      </td>
                      <td className="py-2 pr-4 text-right font-semibold">{formatCurrency(c.balance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between mt-3 text-sm">
              <span className="text-slate-500">{selectedIds.length} selected</span>
              <span className="font-semibold">{formatCurrency(selectedTotal)}</span>
            </div>
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={clearing}>Cancel</Button>
          <Button
            onClick={clear}
            disabled={clearing || selectedIds.length === 0}
            data-testid="confirm-clear-btn"
            className="bg-rose-600 hover:bg-rose-700 text-white"
          >
            {clearing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Clear Receivable {selectedIds.length > 0 ? `(${selectedIds.length})` : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
